<template>
    <div>
        <pc-label
            v-if="label"
            v-bind:label="label"
            v-bind:show-help="showHelp"
            v-bind:help-link="helpLink"
            v-bind:bold="boldLabel"
            v-bind:italic="italicLabel"
            v-on:help-clicked="emit('help-clicked', $event)" />
        <fieldset v-bind:disabled="disabled || readonly">
            <legend
                v-if="label"
                class="d-none">
                {{ label }}
            </legend>
            <div v-bind:class="wrapperClasses">
                <div
                    v-for="option in options"
                    v-bind:key="option.value">
                    <pc-checkbox
                        v-bind:model-value="includedValues.has(option.value)"
                        v-bind:label="option.label"
                        v-bind="getInnerCheckboxBinder(option)"
                        v-on:update:model-value="toggleOption(option.value)" />
                </div>
            </div>
        </fieldset>
    </div>
</template>

<script setup>
    import { computed } from "vue";

    /**
     * Expects a set of values representing the subset of options that
     * are currently selected. Set values should be the same type as
     * option values.
     */
    const includedValues = defineModel("includedValues", { type: Set, required: true });
    const emit = defineEmits(["help-clicked"]);

    const props = defineProps({
        /**
         * Expects an array of objects with the following shape:
         * {
         *    value: string | number,
         *    label: string,
         *    propOverrides: object
         * }
         * Supported inner propOverrides:
         * {
         *   disabled: boolean,
         *   readonly: boolean,
         *   boldLabel: boolean,
         *   italicLabel: boolean
         * }
         */
        options: { type: Array, required: true },

        disabled: { type: Boolean, required: false, default: false },
        readonly: { type: Boolean, required: false, default: false },

        // Outer label props
        label: { type: String, required: false, default: null },
        showHelp: { type: Boolean, required: false, default: false },
        helpLink: { type: String, required: false, default: null },
        boldLabel: { type: Boolean, required: false, default: false },
        italicLabel: { type: Boolean, required: false, default: false },

        // Checkbox appearance props
        inline: { type: Boolean, required: false, default: false },
        switch: { type: Boolean, required: false, default: false },
        useIndeterminate: { type: Boolean, required: false, default: false },

        /**
         * Props to pass to the inner pc-checkbox components.
         * Currently supported props:
         * - boldLabel: boolean
         * - italicLabel: boolean
         */
        checkboxProps: { type: Object, required: false, default: () => ({}) }
    });

    const innerCheckboxBinder = computed(() => {
        return {
            switch: props.switch,
            useIndeterminate: props.useIndeterminate,
            boldLabel: Boolean(props.checkboxProps?.boldLabel),
            italicLabel: Boolean(props.checkboxProps?.italicLabel)
        };
    });

    const toggleOption = (value) => {
        const newIncludedValues = new Set(includedValues.value);
        if (newIncludedValues.has(value)) {
            newIncludedValues.delete(value);
        } else {
            newIncludedValues.add(value);
        }
        includedValues.value = newIncludedValues;
    };
    const wrapperClasses = computed(() => {
        return {
            "d-flex": props.inline,
            "align-items-center": props.inline,
            "gap-3": true
        };
    });

    function getInnerCheckboxBinder(option) {
        if (!option.propOverrides) {
            return innerCheckboxBinder.value;
        }
        return {
            ...innerCheckboxBinder.value,
            disabled: Boolean(option.propOverrides?.disabled),
            readonly: Boolean(option.propOverrides?.readonly),
            boldLabel: Boolean(option.propOverrides?.boldLabel),
            italicLabel: Boolean(option.propOverrides?.italicLabel)
        };
    }
</script>
<style lang="scss" scoped>
    fieldset {
        border: none;
        padding: 0;
        margin: 0;
    }
</style>
